<template>
  <Teleport to="body">
    <Transition name="acn-overlay">
      <div
        v-if="visible"
        class="acn-modal-overlay acn-modal-overlay--glass"
      >
          <div
            ref="modalCardRef"
            class="acn-modal-card acn-modal-card--glass"
            :class="{ 'acn-modal-card--resizing': isResizing }"
            :style="modalCardStyle"
            role="dialog"
            aria-labelledby="avn-modal-title"
            title="Drag the bottom-right corner to resize"
            @click.stop
          >
            <header class="acn-modal-header acn-modal-header--glass">
              <div class="acn-modal-header__title-wrap">
                <span class="acn-modal-header__rule" aria-hidden="true"></span>
                <h2 id="avn-modal-title" class="acn-modal-title">{{ modalTitle }}</h2>
                <span class="acn-modal-header__rule" aria-hidden="true"></span>
              </div>
              <button
                type="button"
                class="acn-modal-close-btn"
                aria-label="Close"
                title="Close"
                @click="handleCancel"
              >
                <i class="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </header>

            <div class="acn-modal-body acn-modal-body--glass">
              <div class="acn-modal-form">
                <div class="acn-modal-form-row acn-modal-form-row--2 acn-modal-form-row--title-tags">
                  <div class="acn-modal-form-group acn-modal-form-group--label-input">
                    <LabelInputField
                      input-id="avn-title"
                      label="Title"
                      v-model="form.title"
                      placeholder="Enter note title"
                      flex
                      :has-error="!!errors.title"
                      @input="clearError('title')"
                    >
                      <div v-if="errors.title" class="acn-modal-error">{{ errors.title }}</div>
                    </LabelInputField>
                  </div>

                  <div class="acn-modal-form-group acn-modal-form-group--tags">
                    <VendorNoteTagsField
                      v-if="companyId"
                      v-model="form.tagIds"
                      :company-id="companyId"
                      select-id-prefix="avn"
                      menu-class="acn-modal-dropdown-menu"
                      allow-create
                    />
                  </div>
                </div>

                <div class="acn-modal-form-group acn-modal-form-group--note-meta">
                  <NoteTypePriorityFields
                    v-if="companyId"
                    :key="noteMetaKey"
                    select-id-prefix="avn"
                    menu-class="acn-modal-dropdown-menu"
                    :company-id="companyId"
                    :note-type="form.noteTypeId"
                    :priority="form.priorityId"
                    allow-create
                    @update:note-type="onNoteTypeChange"
                    @update:priority="onPriorityChange"
                  />
                  <p v-else class="acn-modal-note-meta-hint">
                    Note type and priority lists are unavailable (company not loaded).
                  </p>
                  <div v-if="errors.noteTypeId || errors.priorityId" class="acn-modal-error">
                    {{ errors.noteTypeId || errors.priorityId }}
                  </div>
                </div>

                <div class="acn-modal-form-group acn-modal-form-group--label-description">
                  <LabelDescriptionField
                    input-id="avn-notes"
                    label="Notes"
                    v-model="form.notes"
                    placeholder="Enter note details here..."
                    :rows="5"
                    flex
                    :has-error="!!errors.notes"
                    @update:modelValue="clearError('notes')"
                  >
                    <div v-if="errors.notes" class="acn-modal-error">{{ errors.notes }}</div>
                  </LabelDescriptionField>
                </div>

                <div
                  v-if="isEditMode && existingAttachments.length"
                  class="acn-modal-form-group acn-modal-form-group--existing-attachments"
                >
                  <div class="acn-modal-existing-attachments__label">Existing Attachments</div>
                  <div class="acn-modal-existing-attachments">
                    <span
                      v-for="attachment in existingAttachments"
                      :key="`existing-attachment-${attachment.id}`"
                      class="acn-modal-existing-attachment"
                    >
                      <i class="bi bi-paperclip" aria-hidden="true"></i>
                      {{ attachment.originalFileName || attachment.fileName }}
                    </span>
                  </div>
                </div>

                <div class="acn-modal-form-group acn-modal-form-group--attachments">
                  <CustomerNoteAttachmentsField
                    ref="attachmentsFieldRef"
                    v-model="form.attachments"
                    :label="isEditMode ? 'Add Attachments' : 'Attachments'"
                  />
                </div>

                <div class="acn-modal-form-row acn-modal-form-row--2 acn-modal-form-row--followup">
                  <div class="acn-modal-form-group acn-modal-form-group--label-date">
                    <LabelDateField
                      input-id="avn-follow-up-date"
                      label="Follow-up Date"
                      v-model="form.followUpDate"
                      placeholder="MM/DD/YYYY"
                      flex
                    />
                  </div>
                  <div class="acn-modal-form-group acn-modal-form-group--label-checkbox">
                    <LabelCheckboxField
                      input-id="avn-private-note"
                      label="Private Note"
                      v-model="form.isPrivate"
                      flex
                    />
                  </div>
                </div>
              </div>
            </div>

            <footer class="acn-modal-footer acn-modal-footer--glass">
              <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="handleCancel" />
              <div v-if="saveError" class="acn-modal-footer-error">{{ saveError }}</div>
              <PrimaryButton
                label="Save"
                icon="bi bi-check-lg"
                :loading="isSaving"
                :disabled="isSaving"
                @click="handleSave"
              />
            </footer>
            <div
              class="acn-modal-resize-handle"
              title="Resize"
              aria-hidden="true"
              @mousedown.stop="startResize"
            ></div>
          </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import LabelCheckboxField from '@/modules/consultant/components/LabelCheckboxField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import CustomerNoteAttachmentsField from '@/modules/customer/components/CustomerNoteAttachmentsField.vue';
import VendorNoteTagsField from '@/modules/vendor/components/VendorNoteTagsField.vue';
import NoteTypePriorityFields from '@/components/NoteTypePriorityFields.vue';
import vendorNoteService from '@/services/vendorNote.service.js';
import logger from '@/utils/logger';

const DEFAULT_FORM = () => ({
  title: '',
  noteTypeId: '',
  priorityId: '',
  tagIds: [],
  notes: '',
  attachments: [],
  followUpDate: '',
  isPrivate: false,
});

export default {
  name: 'AddVendorNoteModal',
  components: {
    LabelInputField,
    LabelDescriptionField,
    LabelDateField,
    LabelCheckboxField,
    PrimaryButton,
    SecondaryButton,
    CustomerNoteAttachmentsField,
    VendorNoteTagsField,
    NoteTypePriorityFields,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    vendorId: {
      type: [String, Number],
      default: null,
    },
    vendorName: {
      type: String,
      default: '',
    },
    companyId: {
      type: [String, Number],
      default: null,
    },
    editingNote: {
      type: Object,
      default: null,
    },
  },
  emits: ['cancel', 'close', 'saved'],
  data() {
    return {
      form: DEFAULT_FORM(),
      errors: {},
      saveError: '',
      isSaving: false,
      noteMetaKey: 0,
      modalWidth: 544,
      modalHeight: null,
      isResizing: false,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartWidth: 0,
      resizeStartHeight: 0,
    };
  },
  computed: {
    isEditMode() {
      return !!this.editingNote?.id;
    },
    modalTitle() {
      return this.isEditMode ? 'Edit Vendor Note' : 'Add Vendor Note';
    },
    existingAttachments() {
      return this.editingNote?.attachments || [];
    },
    modalCardStyle() {
      const style = {
        width: `${this.modalWidth}px`,
        maxWidth: '95vw',
      };
      if (this.modalHeight) {
        style.height = `${this.modalHeight}px`;
        style.maxHeight = '92vh';
      }
      return style;
    },
  },
  watch: {
    visible: {
      handler(open) {
        if (open) {
          this.resetForm();
        }
      },
      immediate: true,
    },
    editingNote: {
      handler() {
        if (this.visible) {
          this.resetForm();
        }
      },
    },
  },
  methods: {
    formatDateForPicker(dateString) {
      if (!dateString || String(dateString).trim() === '') return '';

      const trimmed = String(dateString).trim();
      if (trimmed.includes('/')) return trimmed;

      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const [year, month, day] = trimmed.split('-');
        return `${month}/${day}/${year}`;
      }

      const parts = trimmed.split('-');
      if (parts.length === 3) {
        const day = parts[0].padStart(2, '0');
        const monthStr = parts[1];
        const year = parts[2];
        const monthMap = {
          Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
          Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
        };
        const month = monthMap[monthStr];
        if (month) {
          return `${month}/${day}/${year}`;
        }
      }

      return '';
    },
    formatTodayForPicker() {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      return `${month}/${day}/${year}`;
    },
    resetForm() {
      this.noteMetaKey += 1;
      if (this.isEditMode) {
        this.form = {
          title: this.editingNote.title || '',
          noteTypeId: this.editingNote.noteTypeId || '',
          priorityId: this.editingNote.priorityId || '',
          tagIds: [...(this.editingNote.tagIds || this.editingNote.tags?.map((tag) => tag.id) || [])],
          notes: this.editingNote.notes || '',
          attachments: [],
          followUpDate: this.formatDateForPicker(this.editingNote.followUpDate),
          isPrivate: !!this.editingNote.isPrivate,
        };
      } else {
        this.form = {
          ...DEFAULT_FORM(),
          followUpDate: this.formatTodayForPicker(),
        };
      }
      this.errors = {};
      this.saveError = '';
      this.modalWidth = 544;
      this.modalHeight = null;
      this.$refs.attachmentsFieldRef?.resetInput();
    },
    startResize(event) {
      event.preventDefault();
      const card = this.$refs.modalCardRef;
      if (!card) return;

      this.isResizing = true;
      this.resizeStartX = event.clientX;
      this.resizeStartY = event.clientY;
      this.resizeStartWidth = card.offsetWidth;
      this.resizeStartHeight = card.offsetHeight;
      this.modalWidth = this.resizeStartWidth;
      this.modalHeight = this.resizeStartHeight;

      document.addEventListener('mousemove', this.handleResize);
      document.addEventListener('mouseup', this.stopResize);
    },
    handleResize(event) {
      if (!this.isResizing) return;

      const deltaX = event.clientX - this.resizeStartX;
      const deltaY = event.clientY - this.resizeStartY;
      const maxWidth = window.innerWidth - 48;
      const maxHeight = window.innerHeight - 48;

      this.modalWidth = Math.max(320, Math.min(maxWidth, this.resizeStartWidth + deltaX));
      this.modalHeight = Math.max(380, Math.min(maxHeight, this.resizeStartHeight + deltaY));
    },
    stopResize() {
      this.isResizing = false;
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
    },
    clearError(field) {
      if (this.errors[field]) {
        delete this.errors[field];
      }
    },
    onNoteTypeChange(value) {
      this.form.noteTypeId = value || '';
      this.clearError('noteTypeId');
    },
    onPriorityChange(value) {
      this.form.priorityId = value || '';
      this.clearError('priorityId');
    },
    formatDateForBackend(dateString) {
      if (!dateString || String(dateString).trim() === '') return '';

      const trimmed = String(dateString).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }

      if (trimmed.includes('/')) {
        const slashParts = trimmed.split('/');
        if (slashParts.length === 3) {
          return `${slashParts[2]}-${slashParts[0].padStart(2, '0')}-${slashParts[1].padStart(2, '0')}`;
        }
      }

      return '';
    },
    normalizeId(value) {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : null;
    },
    buildNotePayload() {
      const noteTypeId = this.normalizeId(this.form.noteTypeId);
      const priorityId = this.normalizeId(this.form.priorityId);
      const followUpDate = this.formatDateForBackend(this.form.followUpDate);

      return {
        vendorId: this.normalizeId(this.vendorId),
        companyId: this.normalizeId(this.companyId),
        title: this.form.title.trim(),
        noteTypeId,
        priorityId,
        tagIds: (this.form.tagIds || [])
          .map((id) => this.normalizeId(id))
          .filter((id) => id !== null),
        notes: this.form.notes.trim(),
        followUpDate: followUpDate || null,
        isPrivate: !!this.form.isPrivate,
      };
    },
    validate() {
      const errors = {};
      if (!this.vendorId || !this.companyId) {
        this.saveError = 'Vendor or company is missing.';
        return false;
      }
      if (!this.form.title || !this.form.title.trim()) {
        errors.title = 'Title is required.';
      }
      if (!this.form.notes || !this.form.notes.trim()) {
        errors.notes = 'Notes are required.';
      }
      if (!this.normalizeId(this.form.noteTypeId)) {
        errors.noteTypeId = 'Note type is required.';
      }
      if (!this.normalizeId(this.form.priorityId)) {
        errors.priorityId = 'Priority is required.';
      }
      this.errors = errors;
      this.saveError = '';
      return Object.keys(errors).length === 0;
    },
    async uploadNewAttachments(noteId) {
      const pendingFiles = (this.form.attachments || []).filter((file) => file instanceof File);
      if (!pendingFiles.length) return;

      const formData = new FormData();
      formData.append('companyId', String(this.companyId));
      pendingFiles.forEach((file) => {
        formData.append('attachments', file);
      });

      await vendorNoteService.uploadVendorNoteAttachments(noteId, this.companyId, formData);
    },
    handleCancel() {
      this.stopResize();
      this.$emit('cancel');
      this.$emit('close');
    },
    async handleSave() {
      if (!this.validate()) return;
      this.stopResize();
      this.isSaving = true;
      this.saveError = '';

      try {
        const notePayload = this.buildNotePayload();
        let savedNote;

        if (this.isEditMode) {
          const response = await vendorNoteService.updateVendorNote(this.editingNote.id, notePayload);
          savedNote = response?.data?.data;
          if (!savedNote?.id) {
            throw new Error('Note was not updated.');
          }
        } else {
          const response = await vendorNoteService.createVendorNote(notePayload);
          savedNote = response?.data?.data;
          if (!savedNote?.id) {
            throw new Error('Note was not created.');
          }
        }

        await this.uploadNewAttachments(savedNote.id);

        if ((this.form.attachments || []).some((file) => file instanceof File)) {
          const refreshResponse = await vendorNoteService.getVendorNotes(
            this.vendorId,
            this.companyId
          );
          const refreshedNote = (refreshResponse?.data?.data || []).find((note) => note.id === savedNote.id);
          if (refreshedNote) {
            savedNote = refreshedNote;
          }
        }

        this.$emit('saved', savedNote);
        this.$emit('close');
      } catch (error) {
        logger.error('Failed to save vendor note:', error);
        this.saveError = error?.response?.data?.error || error?.message || 'Failed to save note.';
      } finally {
        this.isSaving = false;
      }
    },
  },
  beforeUnmount() {
    this.stopResize();
  },
};
</script>

<style src="@/modules/customer/styles/AddCustomerNoteModal.css"></style>
